import { ColumnGrid } from "@/components/website-builder/ColumnGrid"
import { HEADING_COLOR, type BlockComponentProps } from "./shared"

export function LogoGridBlock({ content, resolved }: BlockComponentProps) {
  const lh = (content?.heading as string) ?? ""
  const lSub = (content?.subheading as string) ?? ""
  const logos =
    (content?.logos as Array<{ name: string; image_url?: string; url?: string; light?: boolean }>) ??
    []
  const lCols = Math.min(5, Math.max(2, (content?.columns as number) ?? 4))
  if (logos.length === 0) return null
  // NOS-238: white source logos (import-flagged `light`) are invisible on the
  // light card background — the whole row gets one uniform dark treatment
  // (mixed treatments look broken). `logo_treatment` overrides: "original"
  // disables it, "dark" forces it.
  const rawTreatment = content?.logo_treatment as string | undefined
  const treatment =
    rawTreatment === "dark" || rawTreatment === "original"
      ? rawTreatment
      : logos.some((l) => l?.light === true)
        ? "dark"
        : "default"
  const logoImgClass =
    treatment === "default"
      ? "max-h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all"
      : "max-h-12 w-auto object-contain"
  const logoImgStyle =
    treatment === "dark" ? { filter: "brightness(0) opacity(0.65)" } : undefined
  const logoGridSectionClass = resolved.hasOverrides
    ? `px-4 @sm:px-6 @md:px-8 ${resolved.paddingClass || "py-8"} ${resolved.cornerClass} ${resolved.alignClass}`.trim()
    : "px-4 py-8 @sm:px-6 @md:px-8"
  return (
    <section className={logoGridSectionClass} style={resolved.sectionStyle}>
      {lh && (
        <h2 className="text-2xl font-bold text-center mb-2" style={{ color: HEADING_COLOR }}>
          {lh}
        </h2>
      )}
      {lSub && <p className="text-center text-muted-foreground mb-6">{lSub}</p>}
      {/* NOS-234: match the standard section content width (max-w-6xl). */}
      <div className="max-w-6xl mx-auto">
        <ColumnGrid columns={lCols} itemCount={logos.length}>
          {logos.map((logo, i) => {
            const inner = logo.image_url ? (
              <img
                src={logo.image_url}
                alt={logo.name || "Logo"}
                className={logoImgClass}
                style={logoImgStyle}
              />
            ) : (
              <span className="text-muted-foreground text-sm">{logo.name || "Logo"}</span>
            )
            return logo.url ? (
              <a
                key={i}
                href={logo.url.startsWith("http") ? logo.url : `https://${logo.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center p-4 rounded-lg border hover:border-muted-foreground/30 transition-colors"
              >
                {inner}
              </a>
            ) : (
              <div
                key={i}
                className="flex items-center justify-center p-4 rounded-lg border"
              >
                {inner}
              </div>
            )
          })}
        </ColumnGrid>
      </div>
    </section>
  )
}
